import React, { useState, useEffect } from 'react';

import { useDebouncedCallback } from 'use-debounce';
import moment from 'moment';

import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
	Loader2,
	MessageSquare,
	SearchIcon,
	StickyNote,
	User,
	Users,
	XIcon,
} from 'lucide-react';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import { maskPhone } from '@/utils/maskPhone';

import AsyncSelect from 'react-select/async';
import { components as selectComponents } from 'react-select';
import { toast } from 'sonner';
import { ActionSheet } from '@/components/mobile';

import axios from 'axios';

import getSearchUrl from '../../../utils/getSearchUrl';

import getWebchatApiUrl from '../../../utils/getWebchatUrl';

import formatPhone from '../../../utils/formatPhoneForUser';
import { notifyConversationAccessDenied } from '@/utils/conversationAccessDenied';
import {
	buildCanonicalCustomerConversationPath,
	buildContextualCustomerConversationPath,
} from '@/utils/customerConversationRoutes';
import { useLocation, useNavigate } from 'react-router-dom';

const DEFAULT_LABEL = 'Type at least 3 characters to search';

// What an empty search offers. Given up front, so the field doesn't show
// its loading dots for a "search" of nothing when it first appears.
const EMPTY_SEARCH_OPTIONS = [{ value: 'more', label: DEFAULT_LABEL }];

const SEARCH_TYPES = [
	{ value: 'Customer', label: 'Customers', Icon: User },
	{ value: 'Message', label: 'Messages', Icon: MessageSquare },
	{ value: 'Notes', label: 'Notes', Icon: StickyNote },
];

const getNoMatchLabel = (searchTerm) => {
	return `No results for ${searchTerm}. Click to create`;
};

const customStyles = {
	container: (base) => ({
		...base,
		// The outer search field anchors the menu, including its leading icon
		// and trailing controls, so results stay aligned when the clear button appears.
		position: 'static',
		width: '100%',
		minWidth: 0,
		fontSize: '0.875rem',
		// 16px on phones so iOS doesn't zoom into the field.
		'@media (max-width: 767px)': { fontSize: '16px' },
	}),
	control: (base) => ({
		...base,
		minHeight: '38px',
		border: 0,
		backgroundColor: 'transparent',
		boxShadow: 'none',
	}),
	valueContainer: (base) => ({ ...base, padding: '2px 0', minWidth: 0 }),
	input: (base) => ({
		...base,
		paddingTop: 0,
		paddingBottom: 0,
		color: 'hsl(var(--foreground))',
	}),
	placeholder: (base) => ({
		...base,
		color: 'hsl(var(--muted-foreground))',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	}),
	singleValue: (base) => ({ ...base, color: 'hsl(var(--foreground))' }),
	dropdownIndicator: (base) => ({ ...base, display: 'none' }),
	indicatorSeparator: (base) => ({ ...base, display: 'none' }),
	menu: (base) => ({
		...base,
		left: 0,
		right: 0,
		width: '100%',
		marginTop: 8,
		marginBottom: 0,
		zIndex: 50,
		borderRadius: 14,
		border: '1px solid hsl(var(--border) / 0.7)',
		backgroundColor: 'hsl(var(--popover))',
		boxShadow: '0 8px 24px -8px rgb(0 0 0 / 0.14), 0 2px 6px -2px rgb(0 0 0 / 0.06)',
		overflow: 'hidden',
	}),
	menuList: (base) => ({
		...base,
		padding: 5,
		maxHeight: 'min(360px, 60vh)',
		// Phones: stop above the keyboard (--app-height is the visible height).
		'@media (max-width: 767px)': {
			maxHeight: 'max(160px, calc(var(--app-height, 100dvh) - 240px))',
		},
	}),
	option: (base, { isDisabled, isFocused, isSelected }) => ({
		...base,
		padding: '10px 9px',
		borderRadius: 9,
		backgroundColor: isFocused || isSelected ? 'hsl(var(--muted) / 0.8)' : 'transparent',
		color: 'hsl(var(--popover-foreground))',
		opacity: isDisabled ? 0.5 : 1,
		cursor: isDisabled ? 'not-allowed' : 'pointer',
		':active': { backgroundColor: 'hsl(var(--muted))' },
	}),
	loadingMessage: (base) => ({
		...base,
		padding: '18px 12px',
		fontSize: '0.8125rem',
		color: 'hsl(var(--muted-foreground))',
	}),
	noOptionsMessage: (base) => ({
		...base,
		padding: '18px 12px',
		fontSize: '0.8125rem',
		color: 'hsl(var(--muted-foreground))',
	}),
};

// The search keyboard's return key reads "Search".
const SearchInput = (inputProps) => (
	<selectComponents.Input {...inputProps} inputMode="search" enterKeyHint="search" />
);
const SELECT_COMPONENTS = { Input: SearchInput };

const SearchResult = ({ customer, name, contact, details, businessName, date, isGroup = false }) => (
	<div className="flex min-w-0 items-start gap-3 text-left font-normal">
		{isGroup ? (
			<div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground" aria-hidden="true">
				<Users className="size-4" strokeWidth={1.75} />
			</div>
		) : (
			<CustomerAvatar customer={customer} fallbackName={name} className="size-9 shrink-0" fallbackClassName="text-xs font-normal" aria-hidden="true" />
		)}
		<div className="min-w-0 flex-1">
			<div className="flex min-w-0 items-baseline justify-between gap-2">
				<span className="truncate text-[15px] leading-5 text-foreground" title={name}>{name}</span>
				{date && (
					<span className="shrink-0 text-[11px] leading-5 text-muted-foreground" title="Last message">
						{moment(date).format('ll')}
					</span>
				)}
			</div>
			{contact && <p className="mt-0.5 truncate text-[13px] leading-5 text-muted-foreground" title={contact}>{contact}</p>}
			{details && <p className="mt-0.5 line-clamp-2 break-words text-xs leading-[18px] text-muted-foreground">{details}</p>}
			{businessName && <p className="mt-0.5 truncate text-xs leading-[18px] text-muted-foreground" title={businessName}>{businessName}</p>}
		</div>
	</div>
);

const CustomerSearchBar = (props) => {
	// The parent keeps the typed text, so it survives this bar being swapped
	// for the bulk ActionBar (a selection on a wider screen) and coming back.
	const [searchBarValueCustomer, setSearchBarValueCustomer] = useState(
		props.initialCustomerSearch || ''
	);
	// Text brought back from a swap is searched again when the bar appears.
	const [defaultOptions] = useState(() =>
		props.initialCustomerSearch ? true : EMPTY_SEARCH_OPTIONS
	);
	const { onCustomerSearchChange } = props;
	useEffect(() => {
		onCustomerSearchChange?.(searchBarValueCustomer);
	}, [onCustomerSearchChange, searchBarValueCustomer]);
	const [searchBarValueMessage, setSearchBarValueMessage] = useState('');
	const [searchBarValueNotes, setSearchBarValueNotes] = useState('');
	const [searchType, setSearchType] = useState('Customer'); // Customer or Message or Notes
	const [searchingByMessage, setSearchingByMessage] = useState(false);
	const [loadingResults, setLoadingResults] = useState(false);
	const [notesResults, setNotesResults] = useState(null); // either null or array
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [isFocused, setIsFocused] = useState(false);
	const [showSearchTypeSheet, setShowSearchTypeSheet] = useState(false);
	const { isMobile } = props;

	// Phones open a result inside the list being viewed, so Back returns to it.
	const getConversationPath = ({ _customerConversation, _business }) =>
		isMobile
			? buildContextualCustomerConversationPath({ pathname, _customerConversation, _business })
			: buildCanonicalCustomerConversationPath({ _customerConversation, _business });

	// Desktop puts the cursor in the composer; on a phone that would raise the keyboard over the thread.
	const focusComposer = () => {
		if (isMobile) return;
		// Delay the focus to avoid the Enter key event from selection being caught by ChatBox
		setTimeout(() => {
			const el = document.getElementById('chatBoxTextBox');
			if (el) {
				el.focus();
			}
		}, 100);
	};

	const handleClearMessageSearch = async () => {
		if (searchingByMessage || searchBarValueMessage) {
			props.clearMessageSearch();
		}

		setIsFocused(false);
		setSearchBarValueCustomer('');
		setSearchBarValueMessage('');
		setSearchingByMessage(false);
	};

	const handleDropDownChange = (value) => {
		setSearchType(value);
		setSearchBarValueCustomer('');
		setSearchBarValueMessage('');
		setIsFocused(false);
	};

	const contactInfo = (customer) => {
		if (!customer) return '';
		let contactLabel = '';

		if (customer.phone) {
			contactLabel = props.isActivated
				? formatPhone(customer.phone)
				: 'XXX XXX XXXX';
		} else if (customer.email) {
			contactLabel = customer.email;
		} else if (customer.facebookUserId) {
			contactLabel = 'Facebook';
		} else if (customer.instagramUserId) {
			contactLabel = 'Instagram';
		} else if (customer.googleConversationId) {
			contactLabel = 'Google Business Message';
		}

		return contactLabel;
	};

	const getLabel = (customer) => {
		if (
			!customer ||
			!customer._business ||
			!props.businessGroup ||
			!props.auth
		) {
			return '';
		}

		const { businessGroup, auth } = props;

		let businessName = '';

		if (auth.user.isEnterpriseEnabled) {
			const matchingBusinesses = businessGroup.filter(
				(b) => b._id === customer._business
			);

			if (matchingBusinesses.length && matchingBusinesses.length > 0) {
				businessName = matchingBusinesses[0].name;
			}
		}

		return (
			<SearchResult
				customer={customer}
				name={maskPhone(customer.name, props.isActivated) || contactInfo(customer) || 'Customer'}
				contact={contactInfo(customer)}
				details={customer.details}
				businessName={businessName}
				date={customer.latestMessageDate}
			/>
		);
	};

	const searchForCustomer = async (searchTerm) => {
		try {
			const safeSearchTerm = searchTerm.replace(/[\W_]+/g, '');

			const isNum = /^\d+$/.test(safeSearchTerm);

			// only ping back end if length is greater than 3
			if (
				(!isNum && safeSearchTerm.length > 2) ||
				(isNum && safeSearchTerm.length > 2)
			) {
				const res = await axios(
					`${getSearchUrl(window.location.href)}/api/customer-search`,
					{
						method: 'post',
						data: {
							searchTerm,
							isEnterpriseRequested:
								props.auth.user.isEnterpriseEnabled,
						},
						withCredentials: true,
					}
				);

				const { matchingCustomers, matchingGroupConversations } =
					res.data;
				const customersWithConversation =
					matchingCustomers?.filter(
						(cust) => !!cust._customerConversation
					) || [];

				// group conversations the matched customers participate in
				const groupOptions = (matchingGroupConversations || [])
					.sort((a, b) => {
						if (a.latestMessageDate && b.latestMessageDate) {
							return (
								new Date(b.latestMessageDate) -
								new Date(a.latestMessageDate)
							);
						}
						return 0;
					})
					.map((group) => ({
						value: {
							_customerConversation: group._customerConversation,
							_business: group._business,
						},
						searchLabel: `${group.name || 'Group conversation'}, group conversation`,
						label: (
							<SearchResult
								isGroup
								name={group.name || 'Group conversation'}
								contact={`Group · ${(group.participants || [])
									.map((p) => maskPhone(p.name || p.phone, props.isActivated))
									.filter(Boolean)
									.join(', ')}`}
								date={group.latestMessageDate}
							/>
						),
					}));

				if (customersWithConversation.length > 0 || groupOptions.length > 0) {
					const options = customersWithConversation
						.sort((a, b) => {
							if (a.latestMessageDate && b.latestMessageDate) {
								return (
									new Date(b.latestMessageDate) -
									new Date(a.latestMessageDate)
								);
							}
							return 0;
						})
						// sort with those starting with the search term to be first in the list
						.sort((a, b) => {
							if (a.name && b.name) {
								const startsWithA = a.name
									.toLowerCase()
									.startsWith(searchTerm.toLowerCase());
								const startsWithB = b.name
									.toLowerCase()
									.startsWith(searchTerm.toLowerCase());

								if (startsWithA && !startsWithB) return -1;
								if (!startsWithA && startsWithB) return 1;
							}
							return 0; // If both or neither start with term, keep  order
						})
						.map((customer) => {
							return {
								value: {
									_customerConversation:
										customer._customerConversation,
									_business: customer._business,
								},
								searchLabel: [
									maskPhone(customer.name, props.isActivated) || 'Customer',
									contactInfo(customer),
									customer.details,
									props.auth.user.isEnterpriseEnabled
										? props.businessGroup?.find((business) => business._id === customer._business)?.name
										: '',
								].filter(Boolean).join(', '),
								label: getLabel(customer),
							};
						});

					return [...options, ...groupOptions];
				} else {
					return [
						{
							value: 'create-new',
							label: getNoMatchLabel(searchTerm),
						},
					];
				}
			}

			return [{ value: 'more', label: DEFAULT_LABEL }];
		} catch {
			return [{ value: 'more', label: DEFAULT_LABEL }];
		}
	};

	const loadSuggestedOptions = useDebouncedCallback(
		(inputValue, callback) => {
			searchForCustomer(inputValue).then((options) => callback(options));
		},
		500
	);

	// handle pressing enter after the text field is focused
	useEffect(() => {
		const handleSearchForMessage = async (searchTerm) => {
			if (!searchTerm) {
				return 'End early';
			}

			if (searchTerm.length < 4) {
				if (props.isMobile) {
					toast.error('Please enter at least 4 letters to search for.');
					return;
				}
				return alert('Please enter at least 4 letters to search for.');
			}

			props.searchForMessage(searchTerm);

			setSearchingByMessage(true);
			setIsFocused(false);
		};

		const handleSearchForNotes = async () => {
			if (!searchBarValueNotes || searchBarValueNotes.length < 3) {
				return '';
			}

			setLoadingResults(true);
			setNotesResults(null);

			const res = await axios.post(
				`${getSearchUrl(window.location.href)}/api/notes-search`,
				{ searchTerm: searchBarValueNotes }
			);

			const { matchingCustomers } = res.data;

			console.log('matchingCustomers', matchingCustomers);

			setNotesResults(matchingCustomers);
		};

		const handleKeyDown = (e) => {
			if (isFocused) {
				// for other devs who might not know keyCodes
				const ENTER_KEY = 13;

				// if we press enter and we are searching by Message search for results
				if (e.keyCode === ENTER_KEY && searchType === 'Message') {
					e.preventDefault();
					handleSearchForMessage(searchBarValueMessage);
				}

				// if we press enter and we are searching by Notes search for results
				if (e.keyCode === ENTER_KEY && searchType === 'Notes') {
					e.preventDefault();
					handleSearchForNotes();
				}
			}
		};

		if (isFocused) {
			window.addEventListener('keydown', handleKeyDown);
		} else {
			window.removeEventListener('keydown', handleKeyDown);
		}

		return () => {
			// Clean up the event listener when the component unmounts
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [
		isFocused,
		props,
		searchBarValueMessage,
		searchBarValueNotes,
		searchType,
	]);

	const handleFocus = () => {
		setIsFocused(true);
	};

	const handleBlur = () => {
		// we cannot call these immediately. Doing so causes the functions to not run.
		setTimeout(() => {
			setNotesResults(null);
		}, 250);

		setTimeout(() => {
			setIsFocused(false);
			setLoadingResults(false);
			setSearchBarValueNotes('');
		}, 500);
	};

	const handleInputChangeMessage = (event) => {
		setSearchBarValueMessage(event.target.value);
	};

	const handleInputChangeNotes = (event) => {
		setSearchBarValueNotes(event.target.value);
	};

	const handleSelectCustomerChange = async (value, action) => {
		console.log('value', value);
		if (!value) {
			return;
		}

		// if user selects Enter more numbers... option, do nothing.
		if (value.value === 'more' || value.label === DEFAULT_LABEL) {
			setSearchBarValueCustomer('');
			return;
		}

		if (value.value === 'create-new') {
			const term = searchBarValueCustomer;
			setSearchBarValueCustomer('');
			navigate('/inbox/new', {
				state: { createCustomerPrefill: term },
			});
			return;
		}

		// if user selects a proper name. get on it. load the customer data, add it to customer data array, and display it.
		if (action.action === 'select-option') {
			setSearchBarValueCustomer('');

			if (!value.value?._customerConversation) {
				return;
			}

			navigate(
				getConversationPath({
					_customerConversation: value.value._customerConversation,
					_business: value.value._business,
				})
			);

			focusComposer();
		}
	};

	const getNotesText = (notes = []) => {
		if (!notes || !notes[0]) return '';
		const note = notes[0];
		const positionOfName = note.indexOf('&$user');
		if (positionOfName === -1) return note;
		return note.slice(0, positionOfName);
	};

	const handleInputChangeCustomer = (event, meta) => {
		// Phones keep the typed search when the field loses focus (opening a
		// conversation, scrolling the list), so it is still there on the way back.
		if (isMobile && (meta?.action === 'input-blur' || meta?.action === 'menu-close')) {
			return;
		}
		// Always update the input value to reflect user's changes
		// The react-select AsyncSelect component handles blur events internally
		setSearchBarValueCustomer(event);
	};

	const handleSelectNotesConversation = async (n) => {
		setSearchBarValueCustomer('');

		const newDataRes = await axios(
			`${getWebchatApiUrl(
				window.location.href
			)}/api/webchat/get-customer-conversation-id`,
			{
				method: 'post',
				data: { _customer: n._id },
				withCredentials: true,
			}
		);

		if (newDataRes.data.error) {
			if (newDataRes.data.accessDenied) {
				notifyConversationAccessDenied();
				return;
			}
			if (isMobile) {
				toast.error('Error getting customer messages');
				return;
			}
			alert('Error getting customer messages');
			return;
		}

		const { _customerConversation, _business } = newDataRes.data;

		navigate(
			getConversationPath({
				_customerConversation,
				_business,
			})
		);

		focusComposer();
	};

	const renderInputs = () => {
		const inputClassName = 'h-[38px] min-w-0 rounded-none border-0 bg-transparent px-0 text-base md:text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0';
		const searchFieldProps = {
			inputMode: 'search',
			enterKeyHint: 'search',
			autoCapitalize: 'none',
			autoCorrect: 'off',
			autoComplete: 'off',
		};

		if (searchType === 'Notes') {
			return (
				<Input
					placeholder="Search customer notes"
					aria-label="Search customer notes"
					className={inputClassName}
					onChange={handleInputChangeNotes}
					onFocus={handleFocus}
					onBlur={handleBlur}
					id="notes-search"
					value={searchBarValueNotes}
					{...searchFieldProps}
				/>
			);
		}

		if (searchType === 'Message') {
			return searchingByMessage ? (
				<div className="w-full">
					<Button
						size="sm"
						variant="ghost"
						className="h-[38px] w-full min-w-0 justify-start px-0 font-normal max-md:text-base max-md:text-primary"
						onClick={handleClearMessageSearch}
					>
						<span className="truncate">{`Clear "${searchBarValueMessage}" results`}</span>
					</Button>
				</div>
			) : (
				<Input
					placeholder="Search for messages"
					aria-label="Search for messages"
					value={searchBarValueMessage}
					className={inputClassName}
					onChange={handleInputChangeMessage}
					onFocus={handleFocus}
					onBlur={handleBlur}
					{...searchFieldProps}
				/>
			);
		}

		return (
			<div className="min-w-0 w-full cursor-text leading-5">
				<AsyncSelect
					inputId="customer-search-bar"
					aria-label="Search for customers"
					noOptionsMessage={() => 'No clients'}
					placeholder="Search for customers"
					inputValue={searchBarValueCustomer}
					value=""
					cacheOptions
					defaultOptions={defaultOptions}
					loadOptions={loadSuggestedOptions}
					styles={customStyles}
					components={SELECT_COMPONENTS}
					getOptionLabel={(option) => option.searchLabel || (typeof option.label === 'string' ? option.label : 'Customer')}
					formatOptionLabel={(option) => option.label}
					classNames={{
						menu: () => 'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150',
						// the dots hold still for Reduce Motion
						loadingIndicator: () => 'motion-reduce:[&>span]:animate-none',
					}}
					onChange={handleSelectCustomerChange}
					onInputChange={handleInputChangeCustomer}
					// isLoading={loadingCustomer}
				/>
			</div>
		);
	};

	const activeSearchType =
		SEARCH_TYPES.find((t) => t.value === searchType) ?? SEARCH_TYPES[0];

	return (
		<div className="relative flex w-full min-w-0 flex-col gap-1">
			<div className="flex h-10 min-w-0 items-center gap-2 rounded-xl border border-input bg-background pl-3 pr-1 transition-shadow max-md:h-11 focus-within:border-foreground/25 focus-within:ring-2 focus-within:ring-foreground/10 max-md:gap-1.5 max-md:rounded-[10px] max-md:border-0 max-md:bg-zinc-200/70 max-md:pl-2.5 max-md:pr-0 max-md:focus-within:ring-0 dark:max-md:bg-zinc-800">
				<SearchIcon className="size-3.5 shrink-0 text-muted-foreground max-md:size-[1.125rem]" aria-hidden="true" />
				<div className="min-w-0 flex-1 text-sm">
					{renderInputs()}
				</div>
				{(searchBarValueCustomer || searchBarValueMessage) && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-7 shrink-0 rounded-lg text-muted-foreground max-md:size-11"
						aria-label="Clear search"
						onClick={handleClearMessageSearch}
					>
						<XIcon className="size-3.5" />
					</Button>
				)}
				{isMobile ? (
					<button
						type="button"
						aria-label={`Search type: ${activeSearchType.label}`}
						aria-haspopup="dialog"
						onClick={() => setShowSearchTypeSheet(true)}
						className="flex size-11 shrink-0 items-center justify-center rounded-[10px] text-muted-foreground active:opacity-50"
					>
						<activeSearchType.Icon className="size-[1.125rem]" />
					</button>
				) : (
				<Select onValueChange={handleDropDownChange} value={searchType}>
					<Tooltip delayDuration={0}>
						<TooltipTrigger asChild>
							<SelectTrigger
								aria-label={`Search type: ${activeSearchType.label}`}
								className="size-8 shrink-0 justify-center rounded-lg border-0 px-0 shadow-none hover:bg-accent focus:ring-0 [&>svg]:hidden"
							>
								<span className="flex">
									<activeSearchType.Icon className="size-4 text-muted-foreground" />
								</span>
							</SelectTrigger>
						</TooltipTrigger>
						<TooltipContent>Search type: {activeSearchType.label}</TooltipContent>
					</Tooltip>
					<SelectContent>
						<SelectGroup>
							{SEARCH_TYPES.map(({ value, label }) => (
								<SelectItem key={value} value={value}>
									{label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
				)}
			</div>
			{isMobile && (
				<ActionSheet
					open={showSearchTypeSheet}
					onOpenChange={setShowSearchTypeSheet}
					title="Search in"
					items={SEARCH_TYPES.map(({ value, label, Icon }) => ({
						id: value,
						label,
						icon: Icon,
						checked: value === searchType,
						onSelect: () => handleDropDownChange(value),
					}))}
				/>
			)}
			{loadingResults && (
				<Card className="absolute top-12 z-50 w-full overflow-hidden rounded-[14px] border-border/70 bg-popover shadow-[0_8px_24px_-8px_rgb(0_0_0/0.14),0_2px_6px_-2px_rgb(0_0_0/0.06)] motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150">
					<CardHeader className="px-4 pb-2 pt-3">
						<CardTitle className="text-xs font-normal text-muted-foreground">
							Matching notes{' '}
							{notesResults &&
								notesResults.length &&
								`(${notesResults.length})`}
						</CardTitle>
					</CardHeader>
					<CardContent className="max-h-[min(360px,60vh)] overflow-y-auto p-1.5 max-md:max-h-[max(160px,calc(var(--app-height,100dvh)-17rem))]">
						{notesResults === null && (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<Loader2 className="my-4 size-4 animate-spin text-muted-foreground" aria-label="Searching notes" />
							</div>
						)}
						{notesResults && notesResults.length === 0 && (
							<div>
								<p className="px-3 py-4 text-[13px] text-muted-foreground">No notes found</p>
							</div>
						)}
						{notesResults && notesResults.length
							? notesResults.map((n) => {
									if (!n) return null;
									return (
										<Button
											key={n._id}
											onClick={() =>
												handleSelectNotesConversation(n)
											}
											variant="ghost"
											className="flex h-auto w-full flex-col items-stretch justify-start gap-1 whitespace-normal rounded-lg px-2 py-2.5 text-left font-normal hover:bg-muted/80"
										>
											{getLabel(n)}
											<p className="pl-12 text-[13px] leading-5 text-muted-foreground line-clamp-2 break-words">{getNotesText(n?.matchingNote ? [n.matchingNote] : n?.notes)}</p>
										</Button>
									);
							  })
							: null}
					</CardContent>
				</Card>
			)}
		</div>
	);
};

export default CustomerSearchBar;
